// ---------- le repère des cases à faire ----------
//
// Le surlignage qui dit où sont les cases restantes du symbole verrouillé. Trois façons de le
// dessiner et trois teintes, au choix du joueur (cf. src/prefs.ts) — parce que ce repère n'a pas
// une seule bonne réponse : il doit tenir sur une feuille nue comme sur un dessin déjà noir
// d'encre, et se voir sans recouvrir ce qu'on est en train de colorier. Les deux exigences
// tirent en sens inverse, et où placer le curseur est une affaire de goût et d'écran.
//
// Les trois ont bougé — un contour qui battait, des hachures qui défilaient, une vague de points
// — et ne bougent plus : c'était la seule animation sans fin du jeu, donc la seule à empêcher la
// boucle de rendu de s'endormir, et elle se voyait sur la batterie des joueurs. Les trois choix
// restent parce qu'ils ne disaient pas que le mouvement : ils disent COMBIEN le repère couvre le
// dessin, ce qui est la question à laquelle ce réglage répond.
//
// Le point, lui, est devenu une CROIX (cf. drawMarkStatic) : rond, il avait la forme et la taille
// de la touche des trois pointillismes et de la pastille des dés, donc sur ces alphabets il se
// lisait comme une case déjà peinte.
//
// Ce module ne dessine rien : il ne porte que le choix et les libellés. Le tracé est dans
// render.ts (drawMarkStatic), les couleurs dans palette.ts, qui lit ici quelle teinte composer.

import { t } from './i18n';

/** Les ids ne décrivent plus tout à fait leurs libellés — « battant » ne bat plus, « cases » est
 *  devenu « une croix par case » — et ils ne changeront pas : setMarkStyle() valide contre cette
 *  liste et retombe sur le défaut, donc renommer un id remettrait silencieusement sur « hachures »
 *  tous ceux qui avaient choisi autre chose. Un id est une clé de stockage, pas un mot. */
export type MarkStyleId = 'battant' | 'hachures' | 'cases';
export type MarkHueId = 'citron' | 'ambre' | 'rose';

export interface MarkStyle {
  id: MarkStyleId;
  label: string;
  hint: string;
}

/** Les trois repères, dans l'ordre où le panneau les propose — un ordre de présentation, pas
 *  une préférence : le défaut est déclaré plus bas et n'a pas à ouvrir la liste.
 *
 *  Le contour garde son mérite, et c'est pour ça qu'il ouvre la rangée : il ne pose rien
 *  PAR-DESSUS les cases, il ne fait qu'en cerner le bord, donc il reste lisible quand il reste
 *  énormément de cases à faire, là où un lavis ou un point par case couvrent la moitié de la
 *  feuille. Le défaut lui préfère pourtant les hachures, et l'arbitrage s'est inversé pour une
 *  raison simple : un bord ne se voit que si on le cherche. En fin de partie les cases restantes
 *  d'un symbole sont des isolats dispersés, et cerner une case seule dessine un cadre de la
 *  taille d'un pixel. Les hachures REMPLISSENT la case, ce qui se trouve du coin de l'œil sans
 *  balayer la feuille. Cet argument-là ne devait rien au défilement, et c'est pourquoi le défaut
 *  n'a pas bougé quand le mouvement est parti. Il vaut plus encore sur les alphabets ronds, où
 *  la trame ne peut ressembler à rien de peint. Le contour reste à une tape pour qui préfère le
 *  dessin intact. */
export const MARK_STYLES: MarkStyle[] = [
  { id: 'battant', label: t('mark.battant.label'), hint: t('mark.battant.hint') },
  { id: 'hachures', label: t('mark.hachures.label'), hint: t('mark.hachures.hint') },
  { id: 'cases', label: t('mark.cases.label'), hint: t('mark.cases.hint') },
];

export interface MarkHue {
  id: MarkHueId;
  label: string;
  /** Préfixe des trois triplets de la charte : `<token>-rgb`, `-light-rgb`, `-deep-rgb`. */
  token: string;
  /** La pastille du panneau — la teinte vive, telle quelle. */
  css: string;
}

/** Le rose par défaut. Sur le papier crème et sous l'encre bleue, c'est celle des trois qui
 *  s'éloigne le plus des deux fonds à la fois : le citron partage sa famille avec l'accent de
 *  toute l'interface, et l'ambre reste voisin du papier. */
export const MARK_HUES: MarkHue[] = [
  { id: 'citron', label: t('mark.hue.citron'), token: '--mark-citron', css: 'rgb(201, 242, 75)' },
  { id: 'ambre', label: t('mark.hue.ambre'), token: '--mark-ambre', css: 'rgb(255, 162, 61)' },
  { id: 'rose', label: t('mark.hue.rose'), token: '--mark-rose', css: 'rgb(255, 92, 168)' },
];

export const DEFAULT_MARK_STYLE: MarkStyleId = 'hachures';
export const DEFAULT_MARK_HUE: MarkHueId = 'rose';

let style: MarkStyleId = DEFAULT_MARK_STYLE;
let hue: MarkHue = MARK_HUES.find((h) => h.id === DEFAULT_MARK_HUE)!;

export function markStyle(): MarkStyleId {
  return style;
}

/** La teinte choisie, pour palette.ts. Un objet et non un id : l'appelant a besoin du préfixe
 *  de token, pas du nom. */
export function markHue(): MarkHue {
  return hue;
}

/** Validés contre les listes, comme l'encre : une valeur retirée d'une version à l'autre ne doit
 *  pas laisser la feuille sans repère du tout. */
export function setMarkStyle(id: string): void {
  style = MARK_STYLES.find((s) => s.id === id)?.id ?? DEFAULT_MARK_STYLE;
}

export function setMarkHue(id: string): void {
  hue = MARK_HUES.find((h) => h.id === id) ?? MARK_HUES.find((h) => h.id === DEFAULT_MARK_HUE)!;
}
